import React, { useEffect, useState } from 'react';

/**
 * AnimatedDashboard — self-contained SVG/CSS analytics dashboard mock.
 * No external assets. Bars animate, sparkline draws, KPI ticks up.
 */
export function AnimatedDashboard({ className = '' }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 4), 2500);
    return () => clearInterval(id);
  }, []);

  const bars = [
    [42, 68, 55, 82, 71, 90, 78],
    [55, 47, 78, 63, 88, 72, 95],
    [30, 62, 48, 74, 66, 84, 70],
    [50, 72, 60, 88, 76, 92, 82],
  ];
  const kpis = [
    { label: 'MRR',      value: ['$142.6K', '$148.3K', '$152.9K', '$159.4K'], delta: '+18.4%' },
    { label: 'Latency',  value: ['84ms', '76ms', '71ms', '68ms'], delta: '−12ms' },
    { label: 'Uptime',   value: ['99.94%', '99.96%', '99.98%', '99.99%'], delta: '+0.05%' },
  ];

  return (
    <div className={`relative rounded-[24px] overflow-hidden shadow-[0_30px_80px_rgba(76,29,149,0.35)] ${className}`}>
      {/* window bg */}
      <div className="relative bg-gradient-to-br from-[#0B0F1A] via-[#141033] to-[#2A0F4C] text-white">
        {/* dotted grid */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(124,58,237,0.35) 1px, transparent 0)',
            backgroundSize: '18px 18px',
          }}
        />
        {/* violet glow */}
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-[#7C3AED]/40 blur-3xl pointer-events-none animate-pulse-glow" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#4C1D95]/45 blur-3xl pointer-events-none" />

        {/* Title bar */}
        <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <span className="ml-3 font-mono text-[10px] tracking-widest text-white/60 uppercase">
            ypnova · analytics.enterprise
          </span>
          <span className="ml-auto inline-flex items-center gap-2 font-mono text-[10px] text-white/70">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
            LIVE
          </span>
        </div>

        {/* KPI row */}
        <div className="relative grid grid-cols-3 gap-3 px-5 pt-5">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-xl bg-white/5 border border-white/10 backdrop-blur px-3 py-2.5">
              <div className="font-mono text-[9px] tracking-widest text-white/50 uppercase">{k.label}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span key={k.value[tick]} className="text-base md:text-lg font-medium tabular-nums transition-opacity">
                  {k.value[tick]}
                </span>
                <span className="font-mono text-[10px] text-emerald-300">{k.delta}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Sparkline (animated draw) */}
        <div className="relative px-5 pt-4">
          <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[9px] tracking-widest text-white/50 uppercase">Revenue · 30d</div>
                <div className="text-lg md:text-xl mt-0.5">+27.8%</div>
              </div>
              <div className="flex gap-1">
                {['1D', '7D', '30D', '90D'].map((t, i) => (
                  <span
                    key={t}
                    className={`font-mono text-[9px] px-2 py-0.5 rounded-full ${
                      i === 2 ? 'bg-[#7C3AED]/40 text-white' : 'text-white/40'
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <svg viewBox="0 0 300 70" className="w-full h-16 mt-2 overflow-visible">
              <defs>
                <linearGradient id="spark" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#7C3AED" />
                  <stop offset="100%" stopColor="#22D3EE" />
                </linearGradient>
                <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0 55 L 25 45 L 50 50 L 75 32 L 100 40 L 125 26 L 150 34 L 175 22 L 200 30 L 225 16 L 250 20 L 275 10 L 300 14 L 300 70 L 0 70 Z"
                fill="url(#sparkFill)"
              />
              <path
                d="M0 55 L 25 45 L 50 50 L 75 32 L 100 40 L 125 26 L 150 34 L 175 22 L 200 30 L 225 16 L 250 20 L 275 10 L 300 14"
                stroke="url(#spark)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="600"
                strokeDashoffset="600"
                style={{ animation: 'sparkDraw 3s ease-out forwards' }}
              />
              {/* dot marker */}
              <circle cx="300" cy="14" r="3" fill="#22D3EE">
                <animate attributeName="r" values="3;5;3" dur="1.6s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>
        </div>

        {/* Bars */}
        <div className="relative px-5 pt-4 pb-5">
          <div className="rounded-xl bg-white/5 border border-white/10 p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[9px] tracking-widest text-white/50 uppercase">
                Weekly deployments
              </div>
              <div className="font-mono text-[9px] text-white/40">Mon → Sun</div>
            </div>
            <div className="flex items-end justify-between gap-2 h-24">
              {bars[tick].map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-[#4C1D95] via-[#7C3AED] to-[#A78BFA] shadow-[0_-2px_8px_rgba(124,58,237,0.35)] transition-[height] duration-700 ease-out"
                    style={{ height: `${h}%`, minHeight: '6px' }}
                  />
                  <div className="font-mono text-[8px] text-white/40">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* keyframes for sparkline */}
      <style>{`
        @keyframes sparkDraw { to { stroke-dashoffset: 0; } }
      `}</style>
    </div>
  );
}
